'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { PERSONAL_DATA_MENU_ID, Position } from '@headless-workspace/config';
import { SideMenuModal, useModalActions } from '@headless-workspace/core-ui';
import { InnerHTML, PersonalDataMenu } from '@headless-workspace/domain/common/client';
import { PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { NewsletterInformation } from '../../../../../src/server';

type NewsletterPolicyProps = PropsWithLocale & {
    newsletterBanner: NewsletterInformation;
    newsletterLegalMention?: NewsletterInformation;
    newsletterOfferLegalMention: NewsletterInformation;
    isNewsletterBoostEnabled: boolean;
};

const PERSONAL_DATA_MENU_SELECTOR = '.btn-personal-info';

export const NewsletterPolicy: Component<NewsletterPolicyProps> = ({
    locale,
    newsletterBanner,
    newsletterLegalMention,
    newsletterOfferLegalMention,
    isNewsletterBoostEnabled,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const { addAndOpen: addAndOpenMenu } = useModalActions();

    const menu = useMemo(
        () =>
            newsletterLegalMention && (
                <PersonalDataMenu
                    modalTitle={newsletterLegalMention.title}
                    description={newsletterLegalMention?.content}
                    offerLegalMention={newsletterOfferLegalMention.content}
                    isNewsletterBoostEnabled={isNewsletterBoostEnabled}
                    locale={locale}
                />
            ),
        [locale, isNewsletterBoostEnabled, newsletterOfferLegalMention, newsletterLegalMention],
    );

    const onOpenMenu = useCallback(() => {
        const personalDataMenuContainerClassName = 'flex flex-col w-full tablet:w-sideMenuTablet';
        addAndOpenMenu(
            PERSONAL_DATA_MENU_ID,
            SideMenuModal({
                side: Position.Right,
                map: { default: menu },
                className: personalDataMenuContainerClassName,
            }),
        );
    }, [addAndOpenMenu, menu]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleClick = (event: Event) => {
            const target = event.target as HTMLElement;
            const personalInfoElement = target.closest(PERSONAL_DATA_MENU_SELECTOR);

            if (personalInfoElement) {
                event.preventDefault();
                event.stopPropagation();
                onOpenMenu();
            }
        };

        container.addEventListener('click', handleClick);
        return () => container.removeEventListener('click', handleClick);
    }, [onOpenMenu]);

    return (
        <div id={newsletterBanner.id} className={'flex-1'} ref={containerRef}>
            <InnerHTML className={'newsletter-banner'} rawHtml={newsletterBanner.content} />
        </div>
    );
};
